import { memo, type ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'
import type { PublicProfile } from '@/lib/watch/types'
import { HostAvatar } from './HostAvatar'
import './watch.css'

export interface WatchInfoProps {
  /** The event's name: the page's `h1`, on one line (an ellipsis when long). */
  title: string
  /** The host of the stage on screen: their compact avatar leads the line. */
  host: PublicProfile
  /** A host is live on the stage while the room is connected: the avatar's live ring. */
  hostLive?: boolean
  /** The quiet line under the name: the page's meta (Live while a camera has video, the audience, the course's length). */
  meta: ReactNode
  /**
   * The line's button's name, which starts with the event's name (what it shows): the Stages sheet's ("…, Shane’s stage.
   * Change stage") with other stages on the event, the event's details ("…, event details") otherwise.
   */
  label: string
  /**
   * The host in words, for screen readers ("Hosted by Transcend, Verified, live on stage"): upright on a phone the chat
   * has no head, so with one stage on the event (the button names the stage otherwise) the avatar says who hosts it.
   */
  hostLabel?: string
  /** What the line opens is open (the button's `aria-expanded`). */
  open: boolean
  /** Opens the Stages sheet or the event's details (the page's). */
  onOpen(): void
  /** What sits on the line's right, outside its button: a host's or a guest's dock. */
  children?: ReactNode
}

/**
 * The phone's info line under the video, as Twitch's channel row (docs/features/watch-experience.md): the stage host's
 * compact avatar (ringed while live; named for screen readers by `hostLabel`), the event's name as the page's heading
 * with the meta under it, and a chevron. One
 * button over the avatar, the words and the chevron opens the Stages sheet when the event has other stages (it folds in
 * the stage button), or the event's details. A host's or a guest's dock follows on the right, outside the button, and
 * wraps under the line when the row runs out of room (watch.css). The heading stays a heading: the button lies over it
 * (the stretched-link pattern), so assistive tech reads the name and then the button. Memoised on plain props.
 */
export const WatchInfo = memo(function WatchInfo({ title, host, hostLive = false, hostLabel, meta, label, open, onOpen, children }: WatchInfoProps) {
  return (
    <div className="watch-info-row">
      <div className="watch-info">
        <HostAvatar host={host} size="compact" live={hostLive} label={hostLabel} className="watch-info__avatar" />
        <div className="watch-info__text">
          <h1 className="watch-info__title">{title}</h1>
          {meta}
        </div>
        <button type="button" className="watch-info__open" aria-label={label} aria-haspopup="dialog" aria-expanded={open} onClick={onOpen}>
          <ChevronDown aria-hidden="true" />
        </button>
      </div>
      {children}
    </div>
  )
})
